<script setup>
import { computed } from 'vue';
import { STATES, REGION_META, stateName } from '../utils/brazilianStates';

const props = defineProps({
  items: { type: Array, default: () => [] },
});

const maxTotal = computed(() =>
  Math.max(1, ...props.items.map(i => i.total || 0))
);

const byRegion = computed(() => {
  const groups = {};
  Object.keys(REGION_META).forEach(r => {
    groups[r] = { total: 0, pos: 0, neg: 0 };
  });
  props.items.forEach(i => {
    const r = i.region || STATES[i.state]?.region;
    if (!r || !groups[r]) return;
    groups[r].total += i.total || 0;
    groups[r].pos += i.positive || 0;
    groups[r].neg += i.negative || 0;
  });
  return Object.entries(groups)
    .map(([r, v]) => ({
      region: r,
      ...v,
      net: v.total ? Math.round(((v.pos - v.neg) / v.total) * 100) : 0,
    }))
    .sort((a, b) => b.total - a.total);
});

const netColor = n => {
  if (n >= 30) return 'text-emerald-700 bg-emerald-100';
  if (n >= 10) return 'text-emerald-600 bg-emerald-50';
  if (n > -10) return 'text-slate-600 bg-slate-100';
  if (n > -30) return 'text-amber-700 bg-amber-100';
  return 'text-rose-700 bg-rose-100';
};
</script>

<template>
  <div class="rounded-xl border border-n-strong/40 bg-n-background p-5">
    <div class="mb-4 flex items-center justify-between">
      <div>
        <h3 class="text-sm font-bold text-n-slate-12">
          Distribuição territorial
        </h3>
        <p class="text-[11px] text-n-slate-11">
          Menções por região e estado (UF inferida)
        </p>
      </div>
      <span class="i-lucide-map size-5 text-n-slate-11" />
    </div>

    <!-- Regions summary -->
    <div class="mb-5 grid grid-cols-5 gap-2">
      <div
        v-for="r in byRegion"
        :key="r.region"
        class="rounded-lg border border-n-strong/30 bg-n-slate-1 p-2 text-center"
      >
        <p class="text-[10px] font-semibold uppercase text-n-slate-11">
          {{ REGION_META[r.region].label }}
        </p>
        <p class="mt-0.5 text-lg font-bold text-n-slate-12">{{ r.total }}</p>
        <span
          :class="[netColor(r.net), 'inline-block rounded px-1.5 py-0.5 text-[9px] font-bold']"
        >
          {{ r.net > 0 ? '+' : '' }}{{ r.net }}%
        </span>
      </div>
    </div>

    <!-- States ranking -->
    <div v-if="items.length" class="space-y-1.5">
      <div
        v-for="i in items.slice(0, 12)"
        :key="i.state"
        class="flex items-center gap-2"
      >
        <span class="w-10 text-xs font-bold text-n-slate-12">{{ i.state }}</span>
        <span class="flex-1 truncate text-[11px] text-n-slate-11">{{ stateName(i.state) }}</span>
        <div class="relative h-4 w-40 overflow-hidden rounded bg-n-slate-2">
          <div
            class="absolute inset-y-0 left-0 bg-blue-500"
            :style="`width: ${(i.total / maxTotal) * 100}%`"
          />
        </div>
        <span class="w-10 text-right text-[11px] font-semibold text-n-slate-12">
          {{ i.total }}
        </span>
        <span
          :class="[netColor(i.sentimentNet), 'w-14 rounded px-1.5 py-0.5 text-center text-[10px] font-bold']"
        >
          {{ i.sentimentNet > 0 ? '+' : '' }}{{ i.sentimentNet }}%
        </span>
      </div>
    </div>

    <div v-else class="py-8 text-center text-xs text-n-slate-11">
      Sem dados territoriais ainda. Rode o backfill (BackfillGeoDemographicsJob) para inferir estados.
    </div>
  </div>
</template>
